import type { ProductQuery, ProductSort, ProductTypeKey } from "@/api";

// The catalog keeps all filter state in the URL (shareable, back-button
// friendly). These helpers translate between URLSearchParams and the
// `ProductQuery` the api seam expects, and count active facets for the
// "Filters (N)" affordance.
export const SORTS: ProductSort[] = ["relevance", "popular", "price-asc", "price-desc", "newest"];

const PAGE_SIZE = 24;

/**
 * Чипы раздела «Товар» (кадры 3845:151131 / 3845:151403). По умолчанию отмечены
 * все три — это «без ограничения», покупатель снимает лишнее. В адресе живут
 * только отмеченные (`type=…`), и только когда отмечены не все.
 */
export const PRODUCT_TYPES: ProductTypeKey[] = ["in-stock", "customizable", "made-to-order"];

/** Отмеченные чипы «Товар». Старая ссылка `inStock=true` читается как «только в наличии». */
export function selectedProductTypes(params: URLSearchParams): ProductTypeKey[] {
	const picked = params.getAll("type").filter((v): v is ProductTypeKey => PRODUCT_TYPES.includes(v as ProductTypeKey));
	if (picked.length > 0) return PRODUCT_TYPES.filter((type) => picked.includes(type));
	if (params.get("inStock") === "true") return ["in-stock"];
	return [...PRODUCT_TYPES];
}

/** Записывает отмеченные чипы «Товар» в адрес: все три — ключ убирается. */
export function withProductTypes(params: URLSearchParams, types: ProductTypeKey[]): URLSearchParams {
	const next = new URLSearchParams(params);
	next.delete("type");
	next.delete("inStock");
	if (types.length > 0 && types.length < PRODUCT_TYPES.length) {
		for (const type of PRODUCT_TYPES) if (types.includes(type)) next.append("type", type);
	}
	next.delete("page");
	return next;
}

export function queryFromParams(params: URLSearchParams): ProductQuery {
	const num = (k: string) => {
		const v = params.get(k);
		return v != null && v !== "" && !Number.isNaN(Number(v)) ? Number(v) : undefined;
	};
	const sort = params.get("sort") as ProductSort | null;
	const types = selectedProductTypes(params);
	const typeFilter = types.length < PRODUCT_TYPES.length;
	return {
		search: params.get("search") || undefined,
		categoryId: params.get("categoryId") || undefined,
		brands: params.getAll("brand"),
		colors: params.getAll("color"),
		sizes: params.getAll("size"),
		priceMin: num("priceMin"),
		priceMax: num("priceMax"),
		// «Только в наличии» — тот же предикат, что и чип «В наличии»; флаг
		// уходит и по-старому, чтобы шлюз без `productType` тоже сузил выдачу.
		inStock: (typeFilter && types.length === 1 && types[0] === "in-stock") || undefined,
		productTypes: typeFilter ? types : undefined,
		sale: params.get("sale") === "true" || undefined,
		sort: sort && SORTS.includes(sort) ? sort : undefined,
		page: num("page") ?? 1,
		pageSize: PAGE_SIZE,
	};
}

// Count of distinct facet selections (everything except search/sort/page) so
// the mobile "Filters" button can show a badge.
export function activeFilterCount(params: URLSearchParams): number {
	let n = 0;
	if (params.get("categoryId")) n += 1;
	n += params.getAll("brand").length;
	n += params.getAll("color").length;
	n += params.getAll("size").length;
	if (params.get("priceMin")) n += 1;
	if (params.get("priceMax")) n += 1;
	if (selectedProductTypes(params).length < PRODUCT_TYPES.length) n += 1;
	if (params.get("sale") === "true") n += 1;
	return n;
}

// Returns a fresh params object with every facet key cleared, preserving the
// free-text search so "reset filters" doesn't drop the user's query.
export function clearedFilters(params: URLSearchParams): URLSearchParams {
	const next = new URLSearchParams();
	const search = params.get("search");
	if (search) next.set("search", search);
	const sort = params.get("sort");
	if (sort) next.set("sort", sort);
	// Сброс фильтров оставляет покупателя в сетке «Все товары», а не уводит на
	// список категорий (тот открывается, когда в адресе нет ни одного фильтра).
	if (!search) next.set("view", "all");
	return next;
}
